"use client";

import { useState } from "react";
import { T } from "@/lib/i18n/T";
import { formatMoney, isDiscountValid } from "@/lib/checkout/api";
import type { Quote } from "@/lib/checkout/types";

/**
 * Order summary rail — line items, a discount field, and the (server-quoted)
 * totals. Purely presentational: it shows whatever `quote` holds and bubbles
 * discount changes up; it never computes totals itself.
 */
export function OrderSummary({
  quote,
  onApplyDiscount,
  onRemoveDiscount,
}: {
  quote: Quote | null;
  onApplyDiscount: (code: string) => void;
  onRemoveDiscount: () => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const currency = quote?.currency ?? "EUR";

  const apply = () => {
    const c = code.trim();
    if (!c) return;
    if (!isDiscountValid(c)) {
      setError(true);
      return;
    }
    setError(false);
    onApplyDiscount(c);
    setCode("");
  };

  return (
    <aside className="rb-co-summary csx3-C00 csx3-w-full csx3-g-16">
      <h3 className="rb-co-summary-title"><T id="checkout.summary.title" /></h3>

      <ul className="rb-co-items csx3-C00 csx3-w-full csx3-g-12">
        {quote?.items.map((it) => (
          <li key={it.id} className="rb-co-item csx3-R00 csx3-g-12 csx3-w-full">
            <span
              className="rb-co-item-img"
              style={{ backgroundImage: `url(${it.image})` }}
              aria-hidden="true"
            />
            <div className="rb-co-item-info csx3-f1 csx3-C00 csx3-g-4">
              <span className="rb-co-item-title">{it.title}</span>
              {it.variant && <span className="rb-co-item-variant">{it.variant}</span>}
              <span className="rb-co-item-qty"><T id="checkout.summary.qty" /> {it.qty}</span>
            </div>
            <span className="rb-co-item-price">{formatMoney(it.unitPrice * it.qty, currency)}</span>
          </li>
        ))}
      </ul>

      {/* Discount code */}
      {quote?.discount.code ? (
        <div className="rb-co-discount-applied csx3-Rs1 csx3-w-full">
          <span className="rb-co-discount-tag">{quote.discount.code}</span>
          <button type="button" className="rb-link-btn" onClick={onRemoveDiscount}>
            <T id="common.remove" />
          </button>
        </div>
      ) : (
        <div className="rb-co-discount csx3-C00 csx3-w-full csx3-g-4">
          <div className="rb-co-discount-row csx3-R01 csx3-g-8 csx3-w-full">
            <input
              className="rb-field-control csx3-f1"
              type="text"
              placeholder="Discount code"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
            />
            <button type="button" className="rb-btn rb-btn--ghost" onClick={apply}>
              <T id="checkout.summary.apply" />
            </button>
          </div>
          {error && <span className="rb-co-discount-error"><T id="checkout.summary.invalidCode" /></span>}
        </div>
      )}

      {/* Totals */}
      <div className="rb-co-totals csx3-C00 csx3-w-full csx3-g-8">
        <Row label={<T id="checkout.summary.subtotal" />} value={quote ? formatMoney(quote.subtotal, currency) : "—"} />
        {quote && quote.discount.amount > 0 && (
          <Row
            label={<T id="checkout.summary.discount" />}
            value={`− ${formatMoney(quote.discount.amount, currency)}`}
            accent
          />
        )}
        <Row
          label={<T id="checkout.summary.shipping" />}
          value={
            !quote || quote.shipping.methodId === null
              ? "—"
              : quote.shipping.amount === 0
                ? "Free"
                : formatMoney(quote.shipping.amount, currency)
          }
        />
        <Row label={<T id="checkout.summary.tax" />} value={quote ? formatMoney(quote.taxAmount, currency) : "—"} />
        <div className="rb-co-divider" />
        <Row
          label={<T id="checkout.summary.total" />}
          value={quote ? formatMoney(quote.total, currency) : "—"}
          strong
        />
      </div>
    </aside>
  );
}

function Row({
  label,
  value,
  strong,
  accent,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  strong?: boolean;
  accent?: boolean;
}) {
  return (
    <div className={`rb-co-row csx3-Rs1 csx3-w-full ${strong ? "is-total" : ""} ${accent ? "is-accent" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
